import { StoreState } from "./storeState";
import { Customer } from "../types/customer";
import { getDefaultTimeZone } from "src/common/types/timeZone";
import { getDefaultBranch } from "src/common/types/branch";
import { DeepLinkStatus } from "../types/deepLink";
import { Representative } from "src/common/types/representative";

const emptyCustomer: Customer = {
  id: -1,
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  last4Ssn: "",
  memberNumber: "",
  member: false
};

const initialState: StoreState = {
  // Auth
  authentication: {
    isCustomer: false,
    isServiceRep: false,
    isSignedIn: false,
    customer: {
      ...emptyCustomer
    },
    error: null,
    serviceRepEmail: ""
  },

  // UI
  appTitleBar: {
    description: "",
    title: ""
  },
  errorMessage: { visible: false },
  holdSecondsRemaining: 300,
  selectedAppointmentValues: {
    additionalInfo: "",
    repSelectCode: "X",
    branch: getDefaultBranch(-1),
    createdBy: "",
    category: {
      id: -1,
      description: "",
      name: "",
      imageUrl: "",
      reasons: [],
      appointmentsEnabled: false,
      membersOnly: false,
      serviceRepOnly: false
    },
    customLocationAddress: "",
    reason: {
      appointmentsEnabled: false,
      id: -1,
      categoryId: -1,
      description: "",
      documentation: "",
      name: "",
      imageUrl: "",
      membersOnly: false,
      serviceRepOnly: false,
      wrapUpDurationInMinutes: -1,
      supportedByAtLeastOneBranch: false
    },
    serviceRep: new Representative(),
    timeSlot: null,
    appLinkAuditKey: "",
    attemptedApptId: -1
  },

  // Data
  appointment: {
    additionalInfo: "",
    branchId: -1,
    confirmationCode: "",
    createdBy: "",
    customerEmail: "",
    customerFirstName: "",
    customerId: -1,
    customerLast4Ssn: "",
    customerLastName: "",
    customerPhone: "",
    customLocationAddress: "",
    customLocationName: "",
    durationInMinutes: 0,
    endDateTimeUtc: "",
    id: -1,
    isConfirmed: false,
    isCustomLocation: false,
    isMobilePhone: false,
    memberNumber: "",
    reasonId: -1,
    serviceRepId: undefined,
    startDateTimeUtc: "",
    timeZone: getDefaultTimeZone(),
    repSelectCode: "X",
    appLinkAuditKey: "",
    linkHonored: false,
    feedbackSubmitted: false,
    feedbackScore: -1,
    virtualAtCreation: false,
    virtualMeetingType: "phone",
    virtualMeetingJoinUrl: ""
  },
  appointments: [],
  appointmentSurvey: {
    id: -1,
    appointment: {
      id: -1,
      appointmentTime: "",
      locationName: "",
      timeZone: getDefaultTimeZone(),
      reasonName: "",
      serviceRepName: ""
    },
    surveyId: -1,
    survey: {
      id: -1,
      userInitiatedFeedbackEnabled: false,
      systemInitiatedFeedbackEnabled: false,
      feedbackDaysBetweenSolicit: -1,
      sampleRate: -1,
      solicitMessage: "",
      threshold: -1,
      enableThresholdNotification: false,
      questions: []
    },
    responses: [],
    freeTextResponse: ""
  },
  appSettings: {
    googleMapsApiKey: "",
    isInternal: false,
    adminHomepageUrl: "",
    skypeAppId: "",
    twilioConfigured: false,
    logoUrl: "./img/logo.jpg",
    palette: {
      primary: {
        main: "",
        contrastText: ""
      },
      secondary: {
        main: "",
        contrastText: ""
      }
    },
    organizationSettings: {
      orgLongName: "",
      orgShortName: "",
      virtualMeetingType: "phone",
      phoneMeetingLocation: "",
      zoomMeetingLocation: "",
      userInitiatedFeedbackEnabled: false,
      feedbackStartExpirationDays: 30,
      feedbackDaysBetweenSolicit: 0,
      usePreferredAgentRouting: false,
      skipAgentSelection: false,
      textIsDisabled: false,
      disableCustomLocations: false,
      appointmentHoldTimeInSeconds: 300,
      appointmentLeadTimeDays: 1,
      appointmentLeadTimeConstraints: {
        minValue: 1,
        maxValue: 365,
        customConstraint: ""
      },
      appointmentWindowDays: 7,
      appointmentWindowConstraints: {
        minValue: 7,
        maxValue: 62,
        customConstraint: ""
      },
      sameDayBufferMinutes: 0,
      sameDayBufferConstraints: {
        minValue: 0,
        maxValue: 360,
        customConstraint: ""
      },
      meetingDurationConstraints: {
        minValue: 0,
        maxValue: 240,
        customConstraint: ""
      },
      wrapUpDurationConstraints: {
        minValue: 0,
        maxValue: 60,
        customConstraint: ""
      }
    },
    skypeSettings: {
      domain: "",
      isSkypeOnPrem: false,
      enableSkype365Feature: false
    }
  },
  branches: [],
  branchHours: [],
  availableServiceReps: [],
  timeSlots: [],
  customerPreferences: {
    calendarInviteEnabled: false,
    communicationPreference: "EMAIL",
    customerId: -1,
    feedbackEnabled: false,
    remindersEnabled: false,
    preferredEmail: "",
    preferredPhone: "",
    isMobilePhone: true
  },
  visitCategories: [],
  videos: {
    path: "",
    videoTitles: []
  },
  deepLink: null,
  deepLinkStatus: new DeepLinkStatus(),
  closeAppAfterAppt: false
};

export { initialState, emptyCustomer };
